import type { DuolingoTtsStatus } from "../api/duolingoTts";
import type { RowData } from "../components/AudioGenerator/EntryScreen/EntryScreen";

import { StorageKey, getStoredItem } from "./storage";

export interface StoredDuolingoTtsItem {
  date: string;
  speed: string;
  ssml?: string;
  status: DuolingoTtsStatus;
  text: string;
  voice: string;
}

// We can't use interface for this because it's eliminated at runtime
const expectedFieldsRequirednessMap = {
  date: true,
  speed: true,
  ssml: false,
  status: true,
  text: true,
  voice: true,
};

const isValidStorableDuolingoTtsRequest = (row: Record<string, RowData>) =>
  Object.entries(expectedFieldsRequirednessMap).every(
    ([key, isRequired]) =>
      typeof row[key] === "string" ||
      (!isRequired && typeof row[key] === "undefined"),
  );

const maybeGetStorableDuolingoTtsRequest = (row: Record<string, RowData>) =>
  isValidStorableDuolingoTtsRequest(row)
    ? (Object.fromEntries(
        Object.entries(row).filter(
          ([key]) => key in expectedFieldsRequirednessMap,
        ),
        // We have to cast to unknown before casting to StoredDuolingoTtsItem because TypeScript
        // doesn't know that our filter combined with the requirement to be prevalidated forces the
        // type to be correct.
      ) as unknown as StoredDuolingoTtsItem)
    : undefined;

export const getStorableDuolingoDuolingoTtsRequests = (obj: object) =>
  Array.isArray(obj)
    ? obj.flatMap(row => {
        const maybeStorableRow = maybeGetStorableDuolingoTtsRequest(row);
        return maybeStorableRow ? [maybeStorableRow] : [];
      })
    : [];

export const getStoredDuolingoTtsHistory = () => {
  const currentPersistedObject = getStoredItem(
    StorageKey.GENERATED_DUOLINGO_TTS,
  );
  return getStorableDuolingoDuolingoTtsRequests(currentPersistedObject);
};
